// Action Queue redesign — dense, email-style triage row.
//
// Replaces the large ActionProposalCard in the queue with a scannable row:
// platform mark, action title + entity/shop, humanized change, requester +
// age, and hover-revealed quick approve/deny. Clicking the row opens the
// detail drawer. Ported from the design's InboxRow onto the real Proposal.
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Proposal } from '../../../lib/api/proposals/proposals-api';
import {
  PlatformMark,
  ReportMark,
  RiskDot,
  ProposalMarkers,
  ChangeInline,
  RequesterAvatar,
  Glyph,
  platformMeta,
  proposalActionTitle,
  proposalEntity,
  shopLabel,
  riskTone,
  timeAgo,
  isClientReportProposal,
  isAwaitingReview,
  clientReportMeta,
  reportTitle,
  reportReviewPath,
} from './proposalFormat';
import { proposalStatusLabel } from './proposalBadges';

interface ProposalRowProps {
  proposal: Proposal;
  active: boolean;
  requesterName?: string;
  onOpen: (proposal: Proposal) => void;
  onApprove: (proposal: Proposal) => void;
  onDeny: (proposal: Proposal) => void;
  showActions?: boolean;
  /** Show the status on the row: for a list that mixes statuses. */
  showStatus?: boolean;
}

const ProposalRow = ({
  proposal,
  active,
  requesterName,
  onOpen,
  onApprove,
  onDeny,
  showActions = true,
  showStatus = false,
}: ProposalRowProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const meta = platformMeta(proposal.platform);
  const title = proposalActionTitle(proposal, t);
  const entity = proposalEntity(proposal);
  const statusBadge = showStatus ? (
    <span
      className={`status-badge status-badge--${proposal.status}`}
      data-testid='aq-row-status'
    >
      {proposalStatusLabel(t, proposal.status)}
    </span>
  ) : null;
  // The left edge carries the risk colour only while the proposal waits; on a
  // finished row it is grey, like the risk dot.
  const barTone = isAwaitingReview(proposal.status)
    ? riskTone(proposal.risk_level)
    : 'muted';

  // Change 2 — client-report review rows render report metadata (brand +
  // period) in place of the budget change, plus a prominent "View report"
  // deep-link into the workbench. Everything else stays identical.
  if (isClientReportProposal(proposal)) {
    const rmeta = clientReportMeta(proposal);
    const path = reportReviewPath(proposal);
    return (
      <div
        className={`aq-irow ${active ? 'aq-irow--active' : ''}`}
        onClick={() => onOpen(proposal)}
        role='button'
        tabIndex={0}
        aria-label={reportTitle(proposal)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(proposal);
          }
        }}
      >
        <span
          className={`aq-irow__bar aq-riskbar--${barTone}`}
          aria-hidden='true'
          data-testid='aq-riskbar'
        />
        <ReportMark size={36} />
        <div className='aq-irow__main'>
          <div className='aq-irow__top'>
            <span className='aq-irow__title'>{reportTitle(proposal)}</span>
            <span className='aq-irow__plat'>{t('ClientReportReview')}</span>
            <RiskDot
              level={proposal.risk_level}
              muted={!isAwaitingReview(proposal.status)}
            />
            <ProposalMarkers proposal={proposal} t={t} />
            {statusBadge}
          </div>
          <div className='aq-irow__sub'>
            <span className='aq-irow__shop'>
              {rmeta.brand || shopLabel(proposal)}
            </span>
            {rmeta.period_label && (
              <>
                <span className='aq-irow__dot'>·</span>
                <span className='aq-irow__entity'>{rmeta.period_label}</span>
              </>
            )}
          </div>
        </div>
        <div className='aq-irow__change'>
          {path && (
            <button
              type='button'
              className='aq-link-btn'
              onClick={(e) => {
                e.stopPropagation();
                navigate(path);
              }}
            >
              <Glyph name='externalLink' size={14} /> {t('ViewReport')}
            </button>
          )}
        </div>
        <div className='aq-irow__meta'>
          <RequesterAvatar name={requesterName} />
          <span className='aq-irow__time'>{timeAgo(proposal.created_at)}</span>
        </div>
        {showActions && (
          <div
            className='aq-irow__actions'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type='button'
              className='aq-qbtn aq-qbtn--approve'
              title={t('ApproveAndPublish')}
              aria-label={t('ApproveAndPublish')}
              onClick={() => onApprove(proposal)}
            >
              <Glyph name='check' size={16} stroke={2.1} />
            </button>
            <button
              type='button'
              className='aq-qbtn aq-qbtn--deny'
              title={t('RequestChanges')}
              aria-label={t('RequestChanges')}
              onClick={() => onDeny(proposal)}
            >
              <Glyph name='x' size={16} stroke={2.1} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`aq-irow ${active ? 'aq-irow--active' : ''}`}
      onClick={() => onOpen(proposal)}
      role='button'
      tabIndex={0}
      aria-label={title}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(proposal);
        }
      }}
    >
      <span
        className={`aq-irow__bar aq-riskbar--${barTone}`}
        aria-hidden='true'
        data-testid='aq-riskbar'
      />
      <PlatformMark platform={proposal.platform} size={36} />
      <div className='aq-irow__main'>
        <div className='aq-irow__top'>
          <span className='aq-irow__title'>{title}</span>
          <span className='aq-irow__plat'>{meta.short}</span>
          <RiskDot
            level={proposal.risk_level}
            muted={!isAwaitingReview(proposal.status)}
          />
          <ProposalMarkers proposal={proposal} t={t} />
          {statusBadge}
        </div>
        <div className='aq-irow__sub'>
          <span className='aq-irow__entity' title={entity.primary}>
            {entity.primary}
          </span>
          {entity.secondary && (
            <span className='aq-irow__entity-id' title={entity.secondary}>
              {entity.secondary}
            </span>
          )}
          <span className='aq-irow__dot'>·</span>
          <span className='aq-irow__shop'>{shopLabel(proposal)}</span>
        </div>
      </div>
      <div className='aq-irow__change'>
        <ChangeInline proposal={proposal} />
      </div>
      <div className='aq-irow__meta'>
        <RequesterAvatar name={requesterName} />
        <span className='aq-irow__time'>{timeAgo(proposal.created_at)}</span>
      </div>
      {showActions && (
        <div
          className='aq-irow__actions'
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type='button'
            className='aq-qbtn aq-qbtn--approve'
            title={t('Approve')}
            aria-label={t('Approve')}
            onClick={() => onApprove(proposal)}
          >
            <Glyph name='check' size={16} stroke={2.1} />
          </button>
          <button
            type='button'
            className='aq-qbtn aq-qbtn--deny'
            title={t('Deny')}
            aria-label={t('Deny')}
            onClick={() => onDeny(proposal)}
          >
            <Glyph name='x' size={16} stroke={2.1} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProposalRow;
